<script setup lang="ts">
/**
 * AI修正(部分修正)。画像編集画面の各画像の「AIで修正」から開く。
 *
 *   1. 修正する範囲を塗る(四角 / 筆 / 消しゴム。OCRのNG枠から範囲を作ることもできる)。範囲は選ばなくてもよい
 *   2. どう修正するかを書き、枚数を選んで「修正を実行」
 *      範囲と指示は1組(OpenAIの画像編集APIは1回の呼び出しで範囲1つ・指示1つのため)。作る枚数は最大3枚
 *   3. 結果を元の画像と見比べ(境目をドラッグ)、再OCRの結果も見て、選んだものを候補(AI)に追加する
 *
 * 範囲の画像(マスク)は「塗った所を不透明・それ以外を透明」のPNGで一時置き場へ上げる。
 * 範囲はAIに「ここを直して」と伝えるためのもの。AIの結果はそのまま使う(元の画像への貼り戻しはしない)。
 * 結果は候補に入るだけで、画像編集画面で登録するまで商品画像は変わらない。
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import * as api from '@/api/endpoints'
import type { AiImageEditJob, AiImageEditResult, AiModelMode, AiQuality } from '@/types/api'
import { useConfirmModalStore } from '@/stores/confirmModal'
import OcrImage from '../OcrImage.vue'
import { ocrHits } from '@/utils/ocrNg'
import { ocrChips, type AiSource } from '@/utils/imageEditor'

const props = defineProps<{
  asin: string
  regionCode: string
  source: AiSource
}>()
const emit = defineEmits<{
  close: []
  /** 結果ができた。画像編集画面はすぐ候補(AI)に入れる(この画面で選んだり追加ボタンを押したりは不要) */
  results: [payload: { job: AiImageEditJob; results: AiImageEditResult[]; sourceLabel: string }]
}>()

const confirmModal = useConfirmModalStore()

/** 今修正している元画像(結果をさらに修正すると、その結果に切り替わる) */
const src = ref<AiSource>(props.source)
/** 候補に入れたときの「〇〇を修正」の〇〇。結果をさらに修正しても最初の画像の名前を使う */
const rootLabel = props.source.label

const phase = ref<'edit' | 'running' | 'result'>('edit')
const errorMsg = ref<string | null>(null)

// ---------------------------------------------------------------------------
// 範囲と指示(複数組)
// ---------------------------------------------------------------------------
/**
 * 範囲と指示の組。範囲は組ごとに別のキャンバス(色違い)に塗る。
 * 範囲を塗らない組は「画像全体への指示」になる。複数の組は上から順に修正する。
 */
interface Step {
  id: number
  color: string
  prompt: string
  painted: boolean
}
const STEP_COLORS = ['#5B3E96', '#D97706', '#0F766E', '#BE185D', '#1D4ED8']
let stepSeq = 0
const steps = ref<Step[]>([])
const activeId = ref(0)
function newStep(): Step {
  const used = new Set(steps.value.map((s) => s.color))
  stepSeq++
  return { id: stepSeq, color: STEP_COLORS.find((c) => !used.has(c)) ?? STEP_COLORS[0], prompt: '', painted: false }
}
function resetSteps() {
  steps.value = []
  const s = newStep()
  steps.value = [s]
  activeId.value = s.id
}
resetSteps()
const activeStep = computed(() => steps.value.find((s) => s.id === activeId.value) ?? steps.value[0])

// ---------------------------------------------------------------------------
// 範囲(マスク)を塗る
// ---------------------------------------------------------------------------
/** マスクの解像度(長辺)。サーバー側で元画像のサイズに合わせて拡大する */
const MASK_LONG = 1024
const HISTORY_MAX = 15

type Tool = 'brush' | 'rect' | 'eraser'
const tool = ref<Tool>('rect')
const brush = ref(40)
const showOcr = ref(true)

/** 組ごとのキャンバス */
const canvases = new Map<number, HTMLCanvasElement>()
const maskSize = ref<{ w: number; h: number } | null>(null)
const history: { id: number; data: ImageData }[] = []
const historyLen = ref(0)

const srcHits = computed(() => ocrHits(src.value.ocr))

// ---- 表示サイズ(枠に収まる最大の大きさを px で決める) ----
const stageEl = ref<HTMLElement | null>(null)
const drawEl = ref<HTMLElement | null>(null)
const stageSize = ref({ w: 0, h: 0 })
const natural = ref<{ w: number; h: number } | null>(null)
const STAGE_PAD = 24
let resizeObserver: ResizeObserver | null = null
const fitStyle = computed(() => {
  const n = natural.value
  const { w, h } = stageSize.value
  if (!n || w <= 0 || h <= 0) return { visibility: 'hidden' as const }
  const scale = Math.min((w - STAGE_PAD * 2) / n.w, (h - STAGE_PAD * 2) / n.h)
  return { width: `${Math.max(1, Math.floor(n.w * scale))}px`, height: `${Math.max(1, Math.floor(n.h * scale))}px` }
})

/** キャンバスを組に結びつける(大きさが違うときだけ合わせる。大きさを変えると中身が消えるため) */
function bindCanvas(id: number, el: HTMLCanvasElement | null) {
  if (!el) {
    canvases.delete(id)
    return
  }
  canvases.set(id, el)
  const m = maskSize.value
  if (m && (el.width !== m.w || el.height !== m.h)) {
    el.width = m.w
    el.height = m.h
  }
}

function ctxOf(id: number) {
  return canvases.get(id)?.getContext('2d', { willReadFrequently: true }) ?? null
}

/** 元画像の実寸からマスクの大きさを決める(元画像が変わったら全部の範囲を作り直す) */
function setupCanvas() {
  const url = src.value.url
  const img = new Image()
  img.onload = () => {
    if (src.value.url !== url) return
    natural.value = { w: img.naturalWidth, h: img.naturalHeight }
    const scale = Math.min(1, MASK_LONG / Math.max(img.naturalWidth, img.naturalHeight))
    const m = { w: Math.max(1, Math.round(img.naturalWidth * scale)), h: Math.max(1, Math.round(img.naturalHeight * scale)) }
    maskSize.value = m
    for (const el of canvases.values()) {
      el.width = m.w
      el.height = m.h
    }
    history.length = 0
    historyLen.value = 0
    for (const s of steps.value) s.painted = false
  }
  img.src = url
}
watch(() => src.value.url, () => nextTick(setupCanvas))

function snapshot(id: number) {
  const c = ctxOf(id)
  const m = maskSize.value
  if (!c || !m) return
  history.push({ id, data: c.getImageData(0, 0, m.w, m.h) })
  if (history.length > HISTORY_MAX) history.shift()
  historyLen.value = history.length
}
function undo() {
  const prev = history.pop()
  historyLen.value = history.length
  if (!prev) return
  const c = ctxOf(prev.id)
  if (!c) return
  c.putImageData(prev.data, 0, 0)
  const s = steps.value.find((x) => x.id === prev.id)
  if (s) s.painted = !isMaskEmpty(prev.id)
}
function clearMask() {
  const s = activeStep.value
  const c = ctxOf(s.id)
  const m = maskSize.value
  if (!c || !m) return
  snapshot(s.id)
  c.clearRect(0, 0, m.w, m.h)
  s.painted = false
}
function isMaskEmpty(id: number) {
  const c = ctxOf(id)
  const m = maskSize.value
  if (!c || !m) return true
  const d = c.getImageData(0, 0, m.w, m.h).data
  for (let i = 3; i < d.length; i += 4) if (d[i] > 0) return false
  return true
}

/** OCRで見つかったNGワードの枠から、選んでいる組の範囲を作る(枠を少し広げて塗る) */
function maskFromOcr() {
  const s = activeStep.value
  const c = ctxOf(s.id)
  const size = src.value.ocr?.size
  const m = maskSize.value
  if (!c || !m || !size || srcHits.value.length === 0) return
  snapshot(s.id)
  const sx = m.w / size[0]
  const sy = m.h / size[1]
  const pad = Math.max(m.w, m.h) * 0.012
  c.globalCompositeOperation = 'source-over'
  c.fillStyle = s.color
  for (const h of srcHits.value) {
    for (const b of h.boxes) {
      c.fillRect(b[0] * sx - pad, b[1] * sy - pad, (b[2] - b[0]) * sx + pad * 2, (b[3] - b[1]) * sy + pad * 2)
    }
  }
  s.painted = true
}

// ---- ポインタ操作(塗るのは選んでいる組のキャンバス) ----
let drawing = false
let last: { x: number; y: number } | null = null
const rectStart = ref<{ x: number; y: number } | null>(null)
const rectNow = ref<{ x: number; y: number } | null>(null)
const cursor = ref<{ x: number; y: number } | null>(null)

function toCanvas(e: PointerEvent) {
  const r = drawEl.value!.getBoundingClientRect()
  const m = maskSize.value ?? { w: r.width, h: r.height }
  return {
    x: ((e.clientX - r.left) * m.w) / r.width,
    y: ((e.clientY - r.top) * m.h) / r.height,
    scale: m.w / r.width,
    dx: e.clientX - r.left,
    dy: e.clientY - r.top,
  }
}

function onPointerDown(e: PointerEvent) {
  const s = activeStep.value
  const c = ctxOf(s.id)
  if (!c || !drawEl.value) return
  drawEl.value.setPointerCapture(e.pointerId)
  const p = toCanvas(e)
  snapshot(s.id)
  drawing = true
  if (tool.value === 'rect') {
    rectStart.value = { x: p.dx, y: p.dy }
    rectNow.value = { x: p.dx, y: p.dy }
    return
  }
  c.globalCompositeOperation = tool.value === 'eraser' ? 'destination-out' : 'source-over'
  c.strokeStyle = s.color
  c.fillStyle = s.color
  c.lineCap = 'round'
  c.lineJoin = 'round'
  c.lineWidth = brush.value * p.scale
  c.beginPath()
  c.arc(p.x, p.y, (brush.value * p.scale) / 2, 0, Math.PI * 2)
  c.fill()
  last = { x: p.x, y: p.y }
  if (tool.value === 'brush') s.painted = true
}
function onPointerMove(e: PointerEvent) {
  if (!drawEl.value) return
  const p = toCanvas(e)
  cursor.value = { x: p.dx, y: p.dy }
  if (!drawing) return
  if (tool.value === 'rect') {
    rectNow.value = { x: p.dx, y: p.dy }
    return
  }
  const c = ctxOf(activeStep.value.id)
  if (!c || !last) return
  c.beginPath()
  c.moveTo(last.x, last.y)
  c.lineTo(p.x, p.y)
  c.stroke()
  last = { x: p.x, y: p.y }
}
function onPointerUp(e: PointerEvent) {
  if (!drawing) return
  drawing = false
  last = null
  const s = activeStep.value
  const c = ctxOf(s.id)
  if (tool.value === 'rect' && c && drawEl.value && rectStart.value && rectNow.value) {
    const r = drawEl.value.getBoundingClientRect()
    const k = (maskSize.value?.w ?? r.width) / r.width
    const x = Math.min(rectStart.value.x, rectNow.value.x) * k
    const y = Math.min(rectStart.value.y, rectNow.value.y) * k
    const w = Math.abs(rectNow.value.x - rectStart.value.x) * k
    const h = Math.abs(rectNow.value.y - rectStart.value.y) * k
    if (w > 2 && h > 2) {
      c.globalCompositeOperation = 'source-over'
      c.fillStyle = s.color
      c.fillRect(x, y, w, h)
      s.painted = true
    }
  }
  rectStart.value = null
  rectNow.value = null
  if (tool.value === 'eraser') s.painted = !isMaskEmpty(s.id)
  drawEl.value?.releasePointerCapture(e.pointerId)
}
function onPointerLeave() {
  cursor.value = null
}
const rectStyle = computed(() => {
  if (!rectStart.value || !rectNow.value) return null
  return {
    left: `${Math.min(rectStart.value.x, rectNow.value.x)}px`,
    top: `${Math.min(rectStart.value.y, rectNow.value.y)}px`,
    width: `${Math.abs(rectNow.value.x - rectStart.value.x)}px`,
    height: `${Math.abs(rectNow.value.y - rectStart.value.y)}px`,
    borderColor: activeStep.value.color,
  }
})
const cursorStyle = computed(() => {
  if (!cursor.value || tool.value === 'rect') return null
  return {
    left: `${cursor.value.x - brush.value / 2}px`,
    top: `${cursor.value.y - brush.value / 2}px`,
    width: `${brush.value}px`,
    height: `${brush.value}px`,
  }
})

// ---------------------------------------------------------------------------
// 保存した指示(このブラウザの localStorage に保存)
// ---------------------------------------------------------------------------
const TEMPLATE_STORAGE_KEY = 'salmon.judge.aiPromptTemplates'

function loadCustomTemplates(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(TEMPLATE_STORAGE_KEY) ?? '[]')
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
  } catch {
    return []
  }
}
const customTemplates = ref<string[]>(loadCustomTemplates())
function saveCustomTemplates() {
  try {
    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(customTemplates.value))
  } catch {
    /* 保存できなくても、この画面の中では使える */
  }
}
function addCustomTemplate() {
  const t = activeStep.value.prompt.trim()
  if (!t || customTemplates.value.includes(t)) return
  customTemplates.value = [...customTemplates.value, t]
  saveCustomTemplates()
}
function removeCustomTemplate(i: number) {
  customTemplates.value = customTemplates.value.filter((_, idx) => idx !== i)
  saveCustomTemplates()
}
function useTemplate(t: string) {
  activeStep.value.prompt = t
}

const count = ref(1)
// ---- モデル・品質(選んだ値はこのブラウザに覚えておく) ----
const MODEL_OPTIONS: { value: AiModelMode; label: string; help: string }[] = [
  { value: 'fast', label: '速度優先', help: 'gpt-image-2.5-flare。早く仕上がります' },
  { value: 'precise', label: '精度優先', help: 'gpt-image-2.5-sunburst。細かい修正の精度を優先します（時間がかかります）' },
]
const QUALITY_OPTIONS: AiQuality[] = ['low', 'medium', 'high', 'xhigh', 'max', 'auto']
const PARAM_STORAGE_KEY = 'salmon.judge.aiEditParams'
function loadParams(): { modelMode: AiModelMode; quality: AiQuality } {
  try {
    const v = JSON.parse(localStorage.getItem(PARAM_STORAGE_KEY) ?? '{}')
    return {
      modelMode: MODEL_OPTIONS.some((o) => o.value === v.modelMode) ? v.modelMode : 'fast',
      quality: QUALITY_OPTIONS.includes(v.quality) ? v.quality : 'high',
    }
  } catch {
    return { modelMode: 'fast', quality: 'high' }
  }
}
const params0 = loadParams()
const modelMode = ref<AiModelMode>(params0.modelMode)
const quality = ref<AiQuality>(params0.quality)
watch([modelMode, quality], () => {
  try {
    localStorage.setItem(PARAM_STORAGE_KEY, JSON.stringify({ modelMode: modelMode.value, quality: quality.value }))
  } catch {
    /* 覚えられなくても、この画面では選んだ値を使う */
  }
})
const modelHelp = computed(() => MODEL_OPTIONS.find((o) => o.value === modelMode.value)?.help ?? '')


// ---------------------------------------------------------------------------
// 実行と結果
// ---------------------------------------------------------------------------
const model = ref<string | null>(null)
const elapsed = ref(0)
/** 実行中の段階(ローディング表示の文言に使う) */
const runStage = ref<'uploading' | 'queued' | 'running'>('uploading')
const runLabel = computed(() =>
  runStage.value === 'uploading' ? '範囲と指示を送っています' : runStage.value === 'queued' ? 'AIを起動しています' : 'AIが修正しています',
)
let timer: ReturnType<typeof setInterval> | null = null
let pollTimer: ReturnType<typeof setTimeout> | null = null
let alive = true
const POLL_MS = 1500
/** 範囲と指示が複数あるとAIを順に呼ぶため長めに待つ(ワーカーの上限15分に合わせる) */
const POLL_LIMIT_MS = 15 * 60_000

/** 指示がすべて入っていれば実行できる(範囲は選ばなくてもよい) */
const canRun = computed(
  () => phase.value === 'edit' && steps.value.length > 0 && steps.value.every((s) => s.prompt.trim().length > 0),
)

function maskBlob(id: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const el = canvases.get(id)
    if (!el) return reject(new Error('範囲を作れませんでした。'))
    el.toBlob((b) => (b ? resolve(b) : reject(new Error('範囲を作れませんでした。'))), 'image/png')
  })
}

async function run() {
  if (!canRun.value) return
  errorMsg.value = null
  phase.value = 'running'
  runStage.value = 'uploading'
  elapsed.value = 0
  const started = Date.now()
  timer = setInterval(() => (elapsed.value = Math.round((Date.now() - started) / 1000)), 1000)
  try {
    // 範囲の画像は並列でアップロードする
    const payloadSteps = await Promise.all(
      steps.value.map(async (s) => {
        let key: string | null = null
        if (s.painted && !isMaskEmpty(s.id)) {
          const blob = await maskBlob(s.id)
          key = (await api.uploadToStaging(blob, 'image/png')).stagingKey
        }
        return { prompt: s.prompt.trim(), maskStagingKey: key }
      }),
    )
    const sv = src.value
    runSource = { ...sv }
    const res = await api.createAiImageEdit({
      asin: props.asin,
      regionCode: props.regionCode,
      source: sv.kind === 'current' ? { imageNo: sv.imageNo } : { stagingKey: sv.stagingKey },
      steps: payloadSteps,
      n: count.value,
      modelMode: modelMode.value,
      quality: quality.value,
    })
    model.value = res.model
    runStage.value = 'queued'
    poll(res.jobId, started)
  } catch (e) {
    fail((e as Error).message)
  }
}

function poll(jobId: string, started: number) {
  pollTimer = setTimeout(async () => {
    if (!alive) return
    try {
      const j = await api.getAiImageEdit(jobId)
      if (!alive) return
      if (j.status === 'done') {
        stopTimer()
        runSeq++
        // この画面で作った結果は消さずに積んでおく(やり直し・さらに修正の後も参照できる)
        runs.value = [{ seq: runSeq, job: j, source: { ...runSource! } }, ...runs.value]
        emit('results', { job: j, results: j.results, sourceLabel: rootLabel })
        activeKey.value = j.results.length ? resultKey(j.jobId, j.results[0].index) : null
        split.value = 50
        viewMode.value = 'after'
        phase.value = 'result'
        return
      }
      if (j.status === 'error') return fail(j.error || 'AI修正に失敗しました。')
      if (j.status === 'running') runStage.value = 'running'
      if (Date.now() - started > POLL_LIMIT_MS) return fail('時間内に終わりませんでした。しばらくしてからお試しください。')
      poll(jobId, started)
    } catch (e) {
      fail((e as Error).message)
    }
  }, POLL_MS)
}
function stopTimer() {
  if (timer) clearInterval(timer)
  timer = null
}
function fail(msg: string) {
  stopTimer()
  errorMsg.value = msg
  phase.value = 'edit'
}

// ---- 結果(この画面で作った分をすべて保持する) ----
/** 1回の「修正を実行」の結果。source はそのとき元にした画像(比べるときの「修正前」) */
interface Run {
  seq: number
  job: AiImageEditJob
  source: AiSource
}
const runs = ref<Run[]>([])
let runSeq = 0
let runSource: AiSource | null = null
const resultKey = (jobId: string, index: number) => `${jobId}:${index}`

const activeKey = ref<string | null>(null)
/** 生成後の表示は「修正後」から */
const viewMode = ref<'split' | 'before' | 'after'>('after')
const split = ref(50)
const active = computed(() => {
  for (const run of runs.value) {
    const r = run.job.results.find((x) => resultKey(run.job.jobId, x.index) === activeKey.value)
    if (r) return { run, result: r }
  }
  return null
})
const activeResult = computed(() => active.value?.result ?? null)
/** 比べるときの「修正前」= その結果を作ったときの元画像 */
const beforeSource = computed(() => active.value?.run.source ?? src.value)
const totalResults = computed(() => runs.value.reduce((n, r) => n + r.job.results.length, 0))

function showResults() {
  if (runs.value.length) phase.value = 'result'
}

let splitting = false
function onSplitDown(e: PointerEvent) {
  splitting = true
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  onSplitMove(e)
}
function onSplitMove(e: PointerEvent) {
  if (!splitting) return
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  split.value = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))
}
function onSplitUp() {
  splitting = false
}

/** 結果をさらに修正する(範囲と指示は作り直し。これまでの結果は残す) */
function editFurther(run: Run, r: AiImageEditResult) {
  src.value = { kind: 'cand', stagingKey: r.stagingKey, url: r.url, ocr: r.ocr, label: `AI 実行${run.seq}-結果${r.index + 1}` }
  resetSteps()
  phase.value = 'edit'
}
/** 指示を変えてやり直す(範囲はそのまま) */
function retry() {
  phase.value = 'edit'
}

async function close() {
  if (phase.value === 'running') {
    if (!(await confirmModal.ask('AIが修正中です。結果を受け取らずに閉じますか？'))) return
  }
  // 作った結果はできた時点で候補(AI)に入っているので、確認せずに閉じる
  emit('close')
}

onMounted(() => {
  nextTick(setupCanvas)
  if (stageEl.value) {
    resizeObserver = new ResizeObserver(() => {
      const el = stageEl.value
      if (el) stageSize.value = { w: el.clientWidth, h: el.clientHeight }
    })
    resizeObserver.observe(stageEl.value)
  }
})
onUnmounted(() => {
  resizeObserver?.disconnect()
  alive = false
  stopTimer()
  if (pollTimer) clearTimeout(pollTimer)
})
</script>

<template>
  <div class="ae-overlay">
    <div class="ae-modal" role="dialog" aria-modal="true" aria-label="AIで修正">
      <div class="ae-head">
        <span class="ae-badge">
          <svg class="icon" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg>
        </span>
        <h2>{{ phase === 'result' ? 'AIで修正 — 結果' : 'AIで修正' }}</h2>
        <span class="ae-crumb"><span class="ae-mono">{{ asin }}</span> ・ {{ src.label }}</span>
        <button class="ae-icon-btn" aria-label="閉じる" @click="close">
          <svg class="icon" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="ae-body">
        <!-- 左: 画像 -->
        <div class="ae-left">
          <!-- 範囲を塗る(結果表示中も塗った範囲を残すため v-show) -->
          <div v-show="phase !== 'result'" class="ae-toolbar">
            <div class="ae-seg" role="group" aria-label="選択ツール">
              <button :aria-pressed="tool === 'brush'" :class="{ on: tool === 'brush' }" @click="tool = 'brush'">
                <svg class="icon" viewBox="0 0 24 24"><path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" /><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" /></svg>筆
              </button>
              <button :aria-pressed="tool === 'rect'" :class="{ on: tool === 'rect' }" @click="tool = 'rect'">
                <svg class="icon" viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14" rx="1" /></svg>四角
              </button>
              <button :aria-pressed="tool === 'eraser'" :class="{ on: tool === 'eraser' }" @click="tool = 'eraser'">
                <svg class="icon" viewBox="0 0 24 24"><path d="M20 20H7L3 16a1 1 0 0 1 0-1.4l9.6-9.6a1 1 0 0 1 1.4 0l5.6 5.6a1 1 0 0 1 0 1.4L12 19" /></svg>消しゴム
              </button>
            </div>
            <label class="ae-range">太さ <input v-model.number="brush" type="range" min="6" max="120" /> <span class="ae-mono">{{ brush }}px</span></label>
            <button
              class="ae-btn ae-btn-ng"
              :disabled="srcHits.length === 0 || phase !== 'edit'"
              :title="srcHits.length === 0 ? 'この画像にはOCRで見つかったNGワードがありません' : 'OCRで見つかったNGワードの枠を範囲にします'"
              @click="maskFromOcr"
            >NG枠から範囲を作る</button>
            <button class="ae-btn" :disabled="historyLen === 0 || phase !== 'edit'" @click="undo">元に戻す</button>
            <button class="ae-btn" :disabled="!activeStep.painted || phase !== 'edit'" @click="clearMask">範囲をクリア</button>
            <span class="ae-grow"></span>
            <label class="ae-check"><input v-model="showOcr" type="checkbox" />OCR枠を表示</label>
          </div>
          <!-- 結果の比較のツールバー -->
          <div v-if="phase === 'result'" class="ae-toolbar">
            <div class="ae-seg" role="group" aria-label="表示方法">
              <button :aria-pressed="viewMode === 'split'" :class="{ on: viewMode === 'split' }" @click="viewMode = 'split'">左右で比べる</button>
              <button :aria-pressed="viewMode === 'before'" :class="{ on: viewMode === 'before' }" @click="viewMode = 'before'">修正前</button>
              <button :aria-pressed="viewMode === 'after'" :class="{ on: viewMode === 'after' }" @click="viewMode = 'after'">修正後</button>
            </div>
            <span class="ae-grow"></span>
            <label class="ae-check"><input v-model="showOcr" type="checkbox" />OCR枠を表示</label>
          </div>

          <!--
            画像の表示枠。表示サイズは「枠に収まる最大の大きさ」をJSで計算して px で指定する
            (割合指定だと、ブラウザを大きくしたとき画像の右に白い余白ができ、OCR枠・範囲がずれたため)。
            範囲(マスク)・OCR枠・比較用の画像は、すべてこの同じ大きさの箱に重ねる。
          -->
          <div ref="stageEl" class="ae-stage">
            <div v-show="phase !== 'result'" class="ae-imgwrap" :class="{ busy: phase === 'running' }" :style="fitStyle">
              <OcrImage :src="src.url" :ocr="src.ocr" :show-all="showOcr" img-class="ae-img" />
              <!-- 組ごとのキャンバス(色違い)。操作は一番上の透明な層で受け、選んでいる組に塗る -->
              <canvas
                v-for="s in steps"
                :key="s.id"
                :ref="(el) => bindCanvas(s.id, el as HTMLCanvasElement | null)"
                class="ae-mask"
                :class="{ active: s.id === activeId }"
              ></canvas>
              <div
                ref="drawEl"
                class="ae-draw"
                @pointerdown="phase === 'edit' && onPointerDown($event)"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
                @pointercancel="onPointerUp"
                @pointerleave="onPointerLeave"
              ></div>
              <div v-if="rectStyle" class="ae-rect" :style="rectStyle"></div>
              <div v-if="cursorStyle && phase === 'edit'" class="ae-cursor" :style="cursorStyle"></div>
              <!-- 実行中: 画像の上を光の帯が走査し、中央でAIのコアが脈打つ -->
              <div v-if="phase === 'running'" class="ae-running" role="status" aria-live="polite">
                <div class="ae-grid" aria-hidden="true"></div>
                <div class="ae-scan" aria-hidden="true"></div>
                <div class="ae-orb" aria-hidden="true">
                  <span class="ae-orb-ring"></span>
                  <span class="ae-orb-ring ae-orb-ring2"></span>
                  <span class="ae-orb-core">
                    <svg viewBox="0 0 24 24"><path d="M12 2.5l2.1 6.1 6.1 2.1-6.1 2.1L12 19l-2.1-6.2-6.1-2.1 6.1-2.1z" /><path d="M19 15.5l.9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9z" /></svg>
                  </span>
                  <span class="ae-spark sp1"></span>
                  <span class="ae-spark sp2"></span>
                  <span class="ae-spark sp3"></span>
                </div>
                <div class="ae-run-text">
                  <b>{{ runLabel }}</b><span class="ae-dots"><i></i><i></i><i></i></span>
                </div>
                <div class="ae-run-sub">
                  {{ elapsed }}秒<template v-if="count > 1"> ・ {{ count }}枚</template>
                </div>
              </div>
            </div>

            <template v-if="phase === 'result' && activeResult">
              <div
                v-if="viewMode === 'split'"
                class="ae-imgwrap ae-compare"
                :style="fitStyle"
                @pointerdown="onSplitDown"
                @pointermove="onSplitMove"
                @pointerup="onSplitUp"
                @pointercancel="onSplitUp"
              >
                <img :src="beforeSource.url" class="ae-img" alt="修正前" draggable="false" />
                <img
                  :src="activeResult.url"
                  class="ae-after"
                  alt="修正後"
                  draggable="false"
                  :style="{ clipPath: `inset(0 0 0 ${split}%)` }"
                />
                <div class="ae-split-line" :style="{ left: `${split}%` }"><span class="ae-split-knob">⇔</span></div>
                <span class="ae-label ae-label-l">修正前</span>
                <span class="ae-label ae-label-r">修正後 · 実行{{ active?.run.seq }}-結果{{ activeResult.index + 1 }}</span>
              </div>
              <div v-else class="ae-imgwrap" :style="fitStyle">
                <OcrImage
                  v-if="viewMode === 'after'"
                  :src="activeResult.url"
                  :ocr="activeResult.ocr"
                  :show-all="showOcr"
                  img-class="ae-img"
                />
                <OcrImage v-else :src="beforeSource.url" :ocr="beforeSource.ocr" :show-all="showOcr" img-class="ae-img" />
              </div>
            </template>
          </div>

          <div class="ae-foot-note">
            <template v-if="phase === 'result' && viewMode === 'split'">境目をドラッグして比べられます。大きさは元の画像と同じです</template>
            <template v-else-if="phase === 'result'">「左右で比べる」で修正前と見比べられます。大きさは元の画像と同じです</template>
            <template v-else>色つきの範囲 = 修正する範囲 ・ 枠 = OCRで見つかったNGワード</template>
          </div>
        </div>

        <!-- 右: 指示 / 結果 -->
        <div class="ae-right">
          <template v-if="phase !== 'result'">
            <button v-if="runs.length > 0" class="ae-history-btn" :disabled="phase === 'running'" @click="showResults">
              これまでの結果（{{ runs.length }}回・{{ totalResults }}枚）を見る
            </button>
            <div class="ae-field">
              <label for="ae-prompt" class="ae-label-strong">どう修正するか</label>
              <span class="ae-muted">
                範囲を塗らなければ画像全体への指示になります（今は{{ activeStep.painted ? '範囲あり' : '範囲なし・画像全体' }}）。
              </span>
              <textarea id="ae-prompt" v-model="activeStep.prompt" rows="4" :disabled="phase === 'running'"></textarea>
            </div>
            <div class="ae-field">
              <span class="ae-label-strong">保存した指示</span>
              <div class="ae-chips">
                <span v-for="(t, i) in customTemplates" :key="i" class="ae-chip-wrap">
                  <button class="ae-chip" :class="{ on: activeStep.prompt === t }" :title="t" :disabled="phase === 'running'" @click="useTemplate(t)">
                    {{ t.length > 18 ? t.slice(0, 18) + '…' : t }}
                  </button>
                  <button class="ae-chip-x" aria-label="この指示を消す" title="消す" @click="removeCustomTemplate(i)">×</button>
                </span>
                <button class="ae-chip ae-chip-add" :disabled="!activeStep.prompt.trim()" @click="addCustomTemplate">＋ 今の指示を保存</button>
              </div>
            </div>
            <div class="ae-field">
              <span class="ae-label-strong">モデル</span>
              <div class="ae-seg" role="group" aria-label="モデル">
                <button
                  v-for="o in MODEL_OPTIONS"
                  :key="o.value"
                  :aria-pressed="modelMode === o.value"
                  :class="{ on: modelMode === o.value }"
                  :disabled="phase === 'running'"
                  :title="o.help"
                  @click="modelMode = o.value"
                >{{ o.label }}</button>
              </div>
              <span class="ae-muted">{{ modelHelp }}</span>
            </div>
            <div class="ae-field">
              <label for="ae-quality" class="ae-label-strong">品質</label>
              <select id="ae-quality" v-model="quality" class="ae-select" :disabled="phase === 'running'">
                <option v-for="q in QUALITY_OPTIONS" :key="q" :value="q">{{ q }}</option>
              </select>
            </div>
            <div class="ae-field">
              <span class="ae-label-strong">作る枚数</span>
              <div class="ae-seg ae-seg-count" role="group" aria-label="作る枚数">
                <button
                  v-for="n in 3"
                  :key="n"
                  :aria-pressed="count === n"
                  :class="{ on: count === n }"
                  :disabled="phase === 'running'"
                  @click="count = n"
                >{{ n }}</button>
              </div>
            </div>
            <div v-if="errorMsg" class="ae-error">{{ errorMsg }}</div>
            <span class="ae-grow"></span>
            <div class="ae-actions">
              <button class="btn btn-ghost" @click="close">キャンセル</button>
              <button class="btn ae-run" :disabled="!canRun" @click="run">
                <span v-if="phase === 'running'" class="spinner spinner-sm"></span>
                {{ phase === 'running' ? '修正中…' : '修正を実行' }}
              </button>
            </div>
          </template>

          <template v-else>
            <span class="ae-label-strong">結果（新しい順・クリックで左に表示）</span>
            <div class="ae-hists">
              <div v-for="run in runs" :key="run.job.jobId" class="ae-hist">
                <div class="ae-summary">
                  <span class="ae-hist-head"><b>実行{{ run.seq }}</b><span class="ae-muted">{{ run.source.label }} を修正 ・ {{ run.job.model }} ・ {{ run.job.quality }}</span></span>
                  <span class="ae-hist-prompt">{{ run.job.prompt }}</span>
                </div>
                <div class="ae-results">
                  <div
                    v-for="r in run.job.results"
                    :key="r.index"
                    class="ae-result"
                    :class="{ active: activeKey === resultKey(run.job.jobId, r.index) }"
                    :title="'クリックで左に表示'"
                    @click="activeKey = resultKey(run.job.jobId, r.index)"
                  >
                    <img :src="r.url" :alt="`結果${r.index + 1}`" />
                    <div class="ae-result-info">
                      <b>結果{{ r.index + 1 }}</b>
                      <span class="ae-result-chips">
                        <span class="ae-muted">再OCR:</span>
                        <span v-for="(oc, i) in ocrChips(r.ocr, false, '未確認')" :key="i" :class="oc.cls" :title="oc.title">{{ oc.text }}</span>
                        <span v-if="r.ocr.status === 'unavailable'" class="ae-muted">OCRできませんでした</span>
                      </span>
                      <button class="ae-btn ae-btn-ai" @click.stop="editFurther(run, r)">この結果をさらに修正</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="ae-note">作った画像はすべて、画像編集画面の候補「AI」に入っています。要らないものは候補の「削除」で外せます。</div>
            <span class="ae-grow"></span>
            <div class="ae-actions">
              <button class="btn btn-ghost" @click="retry">指示を変えてやり直す</button>
              <button class="btn btn-primary" @click="close">閉じる</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ae-overlay{
  position:fixed;inset:0;z-index:1080;background:rgba(10,14,20,.6);
  display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn .12s ease;
}
.ae-modal{
  width:min(1300px,100%);max-height:100%;background:var(--panel-alt);border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.35);
  display:flex;flex-direction:column;overflow:hidden;
}
.ae-head{display:flex;align-items:center;gap:12px;padding:12px 18px;background:var(--panel);border-bottom:1px solid var(--line);flex-wrap:wrap;}
.ae-head h2{margin:0;font-size:17px;font-weight:700;}
.ae-badge{width:30px;height:30px;border-radius:6px;background:#EFEAF8;color:#5B3E96;display:inline-flex;align-items:center;justify-content:center;}
.ae-crumb{flex:1 1 200px;font-size:13px;color:var(--ink-faint);}
.ae-mono{font-family:var(--font-mono);}
.ae-icon-btn{width:34px;height:34px;border-radius:8px;border:1px solid var(--line);background:var(--panel);display:inline-flex;align-items:center;justify-content:center;color:var(--ink-soft);}
.ae-body{display:flex;flex-wrap:wrap;align-items:stretch;flex:1;min-height:0;overflow:auto;}
.ae-left{flex:999 1 620px;min-width:0;display:flex;flex-direction:column;border-right:1px solid var(--line);}
.ae-right{flex:1 1 360px;min-width:0;display:flex;flex-direction:column;gap:16px;padding:16px 18px;background:var(--panel);}

.ae-toolbar{display:flex;align-items:center;flex-wrap:wrap;gap:8px 12px;padding:10px 14px;background:var(--panel);border-bottom:1px solid var(--line-soft);}
.ae-seg{display:inline-flex;gap:3px;background:var(--bg);border-radius:8px;padding:3px;}
.ae-seg button{
  display:inline-flex;align-items:center;gap:5px;height:32px;padding:0 12px;border:1px solid transparent;background:transparent;
  border-radius:6px;font-size:12.5px;color:var(--ink-soft);
}
.ae-seg button.on{background:var(--panel);border-color:var(--brand-tint-line);color:var(--brand);font-weight:700;}
.ae-seg-count{align-self:flex-start;}
.ae-seg button:disabled{opacity:.5;cursor:not-allowed;}
.ae-seg-count button{width:44px;justify-content:center;}
.ae-range{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;}
.ae-range input{width:110px;accent-color:var(--brand);}
.ae-btn{height:32px;padding:0 12px;border:1px solid var(--line);background:var(--panel);border-radius:6px;font-size:12.5px;color:var(--ink-soft);}
.ae-btn:disabled{opacity:.5;cursor:not-allowed;}
.ae-btn-ng{border-color:var(--ng-line);background:var(--ng-tint);color:#A62F22;font-weight:600;}
.ae-btn-ai{border-color:#D4C8EC;color:#5B3E96;height:28px;font-size:12px;align-self:flex-start;}
.ae-check{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;}
.ae-check input{accent-color:var(--brand);}
.ae-grow{flex:1 1 auto;}

.ae-stage{
  flex:1 0 auto;background:var(--image-stage);display:flex;align-items:center;justify-content:center;overflow:hidden;
  height:clamp(420px, calc(100vh - 240px), 960px);min-height:420px;
}
.ae-imgwrap{position:relative;flex:none;line-height:0;box-shadow:0 2px 10px rgba(0,0,0,.18);background:#fff;touch-action:none;}
/* 画像・OCR枠の箱を、表示枠(.ae-imgwrap)とぴったり同じ大きさにする */
.ae-imgwrap :deep(.ocr-img-wrap){display:block;width:100%;height:100%;max-width:none;}
.ae-imgwrap :deep(.ae-img), .ae-imgwrap > .ae-img{display:block;width:100%;height:100%;max-width:none;max-height:none;}
.ae-mask{position:absolute;inset:0;width:100%;height:100%;opacity:.3;pointer-events:none;}
.ae-mask.active{opacity:.5;}
.ae-draw{position:absolute;inset:0;cursor:crosshair;}
.ae-imgwrap.busy .ae-draw{cursor:wait;}
.ae-painting{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--ink-soft);}
.ae-step-dot{width:10px;height:10px;border-radius:5px;flex:none;display:inline-block;}
.ae-step{display:flex;flex-direction:column;gap:6px;padding:8px 10px;border:1px solid var(--line);border-radius:8px;cursor:pointer;background:var(--panel);}
.ae-step.active{border-color:var(--step-color);box-shadow:0 0 0 1px var(--step-color);}
.ae-step-head{display:flex;align-items:center;gap:6px;font-size:13px;}
.ae-step textarea{font-size:14px;line-height:1.6;padding:8px 10px;border:1px solid var(--mono-line);border-radius:6px;resize:vertical;}
.ae-step-del{height:24px;padding:0 8px;border:1px solid var(--ng-line);background:var(--panel);color:var(--ng);border-radius:5px;font-size:11.5px;}
.ae-add-step{align-self:flex-start;}
.ae-rect{position:absolute;border:2px dashed #5B3E96;background:rgba(0,0,0,.08);pointer-events:none;}
.ae-cursor{position:absolute;border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,.45);pointer-events:none;}
.ae-running{
  position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;overflow:hidden;
  background:radial-gradient(ellipse at center, rgba(30,20,70,.35) 0%, rgba(12,10,30,.62) 100%);
  backdrop-filter:blur(2px) saturate(1.15);-webkit-backdrop-filter:blur(2px) saturate(1.15);
  color:#fff;line-height:1.5;
}
/* うっすらとしたグリッド */
.ae-grid{
  position:absolute;inset:0;pointer-events:none;opacity:.35;
  background-image:linear-gradient(rgba(196,181,253,.18) 1px, transparent 1px),linear-gradient(90deg, rgba(196,181,253,.18) 1px, transparent 1px);
  background-size:28px 28px;
  mask-image:radial-gradient(ellipse at center, #000 30%, transparent 75%);-webkit-mask-image:radial-gradient(ellipse at center, #000 30%, transparent 75%);
}
/* 上から下へ走る光の帯 */
.ae-scan{
  position:absolute;left:0;right:0;top:-40%;height:40%;pointer-events:none;
  background:linear-gradient(180deg, transparent 0%, rgba(167,139,250,.0) 30%, rgba(167,139,250,.38) 80%, rgba(236,233,254,.75) 97%, transparent 100%);
  animation:ae-scan 2.4s cubic-bezier(.45,.05,.55,.95) infinite;
}
@keyframes ae-scan{from{transform:translateY(0);}to{transform:translateY(350%);}}
/* 中央のAIコア */
.ae-orb{position:relative;width:96px;height:96px;}
.ae-orb-ring{
  position:absolute;inset:0;border-radius:50%;
  background:conic-gradient(from 0deg, #7C3AED, #22D3EE, #F472B6, #A78BFA, #7C3AED);
  -webkit-mask:radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px));
  mask:radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px));
  filter:drop-shadow(0 0 10px rgba(167,139,250,.85));
  animation:ae-spin 1.6s linear infinite;
}
.ae-orb-ring2{inset:-10px;opacity:.45;animation:ae-spin 3.2s linear infinite reverse;filter:blur(1px) drop-shadow(0 0 14px rgba(34,211,238,.6));}
.ae-orb-core{
  position:absolute;inset:12px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  background:radial-gradient(circle at 32% 28%, #FFFFFF 0%, #EDE9FE 38%, #A78BFA 100%);
  box-shadow:0 0 22px rgba(167,139,250,.9), inset 0 0 12px rgba(91,33,182,.35);
  animation:ae-pulse 1.6s ease-in-out infinite;
}
.ae-orb-core svg{width:38px;height:38px;fill:#5B21B6;animation:ae-twinkle 1.6s ease-in-out infinite;}
.ae-spark{
  position:absolute;top:50%;left:50%;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;
  background:#fff;box-shadow:0 0 8px 2px rgba(196,181,253,.95);
  animation:ae-orbit 2.6s linear infinite;
}
.ae-spark.sp2{width:4px;height:4px;margin:-2px 0 0 -2px;animation-duration:3.4s;animation-delay:-1.1s;box-shadow:0 0 8px 2px rgba(34,211,238,.9);}
.ae-spark.sp3{width:5px;height:5px;animation-duration:2.1s;animation-delay:-.6s;box-shadow:0 0 8px 2px rgba(244,114,182,.9);}
@keyframes ae-spin{to{transform:rotate(360deg);}}
@keyframes ae-pulse{0%,100%{transform:scale(.94);}50%{transform:scale(1.05);}}
@keyframes ae-twinkle{0%,100%{transform:rotate(0) scale(1);opacity:.9;}50%{transform:rotate(18deg) scale(1.12);opacity:1;}}
@keyframes ae-orbit{from{transform:rotate(0deg) translateX(60px);}to{transform:rotate(360deg) translateX(60px);}}
/* 文字は光が流れる */
.ae-run-text{position:relative;display:flex;align-items:baseline;gap:4px;font-size:15px;letter-spacing:.06em;}
.ae-run-text b{
  font-weight:700;color:transparent;
  background:linear-gradient(90deg, #FFFFFF 0%, #C4B5FD 40%, #67E8F9 55%, #FFFFFF 80%);background-size:200% 100%;
  -webkit-background-clip:text;background-clip:text;animation:ae-shimmer 2.2s linear infinite;
}
@keyframes ae-shimmer{from{background-position:200% 0;}to{background-position:-200% 0;}}
.ae-dots{display:inline-flex;gap:3px;}
.ae-dots i{width:4px;height:4px;border-radius:2px;background:#C4B5FD;animation:ae-dot 1.2s ease-in-out infinite;}
.ae-dots i:nth-child(2){animation-delay:.15s;}
.ae-dots i:nth-child(3){animation-delay:.3s;}
@keyframes ae-dot{0%,80%,100%{transform:translateY(0);opacity:.4;}40%{transform:translateY(-4px);opacity:1;}}
.ae-run-sub{position:relative;font-family:var(--font-mono);font-size:12px;color:rgba(237,233,254,.85);}
@media (prefers-reduced-motion: reduce){
  .ae-scan,.ae-orb-ring,.ae-orb-core,.ae-orb-core svg,.ae-spark,.ae-run-text b,.ae-dots i{animation:none;}
}
.ae-compare{cursor:ew-resize;user-select:none;}
.ae-after{position:absolute;inset:0;width:100%;height:100%;display:block;}
.ae-split-line{position:absolute;top:0;bottom:0;width:2px;margin-left:-1px;background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.25);pointer-events:none;}
.ae-split-knob{
  position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:17px;background:#fff;
  box-shadow:0 1px 4px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:14px;line-height:1;color:var(--ink-soft);
}
.ae-label{position:absolute;top:10px;font-size:12px;font-weight:700;color:#fff;padding:2px 8px;border-radius:4px;line-height:1.5;pointer-events:none;}
.ae-label-l{left:10px;background:rgba(31,36,48,.8);}
.ae-label-r{right:10px;background:#5B3E96;}
.ae-foot-note{padding:8px 14px;background:var(--panel);border-top:1px solid var(--line-soft);font-size:12px;color:var(--ink-faint);}

.ae-field{display:flex;flex-direction:column;gap:6px;}
.ae-label-strong{font-size:13px;font-weight:700;}
.ae-field textarea{font-size:14px;line-height:1.6;padding:10px 12px;border:1px solid var(--mono-line);border-radius:6px;resize:vertical;}
.ae-chips{display:flex;flex-wrap:wrap;gap:6px;}
.ae-chip{height:30px;padding:0 12px;border:1px solid var(--line);background:var(--panel);border-radius:15px;font-size:12px;color:var(--ink-soft);}
.ae-chip.on{border-color:#5B3E96;background:#EFEAF8;color:#5B3E96;font-weight:700;}
.ae-chip-add{border-style:dashed;color:var(--ink-faint);}
.ae-chip-wrap{display:inline-flex;align-items:center;}
.ae-chip-x{border:none;background:transparent;color:var(--ink-faint);font-size:13px;padding:0 4px;}
.ae-info{font-size:12px;line-height:1.6;color:var(--brand-dark);background:var(--brand-tint);border:1px solid var(--brand-tint-line);border-radius:8px;padding:10px 12px;}
.ae-error{font-size:12.5px;color:var(--ng);background:var(--ng-tint);border:1px solid var(--ng-line);border-radius:8px;padding:8px 12px;}
.ae-actions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;}
.ae-actions .btn{padding:10px 18px;font-size:13px;}
.ae-run{background:#5B3E96;border-color:#47307A;color:#fff;}
.ae-run:hover:not(:disabled){background:#47307A;}
.ae-run:disabled{background:var(--panel);border-color:var(--line);color:var(--ink-faint);}

.ae-summary{display:flex;flex-direction:column;gap:4px;padding:10px 12px;background:var(--panel-alt);border:1px solid var(--line-soft);border-radius:8px;font-size:13px;line-height:1.6;}
.ae-muted{font-size:11.5px;color:var(--ink-faint);}
.ae-results{display:flex;flex-direction:column;gap:10px;}
.ae-hists{display:flex;flex-direction:column;gap:16px;overflow-y:auto;max-height:max(240px,calc(100vh - 440px));padding-right:4px;}
.ae-hist{display:flex;flex-direction:column;gap:8px;}
.ae-hist-head{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;}
.ae-hist-prompt{white-space:pre-wrap;}
.ae-history-btn{
  align-self:stretch;height:36px;border:1px solid #D4C8EC;background:#EFEAF8;color:#5B3E96;border-radius:8px;
  font-size:13px;font-weight:600;
}
.ae-history-btn:hover:not(:disabled){background:#E3DAF4;}
.ae-select{height:34px;padding:0 10px;border:1px solid var(--mono-line);border-radius:6px;font-size:13px;background:var(--panel);align-self:flex-start;min-width:140px;}
.ae-result{display:flex;gap:10px;align-items:flex-start;padding:10px;border:1px solid var(--line);border-radius:8px;cursor:pointer;background:var(--panel);}
.ae-result.active{border:2px solid #5B3E96;background:#EFEAF8;padding:9px;}
.ae-result img{width:96px;height:96px;object-fit:contain;background:#fff;border:1px solid var(--line);border-radius:6px;flex:none;}
.ae-result-info{display:flex;flex-direction:column;gap:6px;min-width:0;font-size:13px;}
.ae-result-chips{display:flex;align-items:center;gap:4px;flex-wrap:wrap;}
.ae-note{font-size:12px;line-height:1.6;color:#6B4A10;background:var(--hold-tint);border:1px solid var(--hold-line);border-radius:8px;padding:8px 12px;}
</style>
